<script lang="ts">
  import Rating, { RatingType } from 'src/components/rating.svelte';
  import { IS_PROD } from 'src/config';
  import { BusinessReviewEntryType } from 'src/function-utils/data-models';
  import Like from 'src/icons/like.svelte';
  import { formatReviewCardDate } from 'src/utils/date';
  import Avatar from './Avatar.svelte';
  import ReviewSourceIcon from './ReviewSourceIcon.svelte';

  export let message: string;
  export let user: {
    name: string;
    pictureUrl?: string;
    avatarUrl?: string;
  };
  export let date: number | string | void;
  export let reviewOriginUrl: string = '';
  export let reviewType: BusinessReviewEntryType;
  export let businessKey: string;
  export let rating: number = 0;
  export let ratingMax: number = 0;
  export let recommended: boolean = null;
  export let isCelebrity: boolean = false;
  export let disableTruncate: boolean = false;

  const MAX_MESSAGE_LENGTH = 150;

  let showTruncatedMessage = true;

  $: isSeeAll = reviewType === BusinessReviewEntryType.SEE_ALL;
  $: truncateMessage = disableTruncate
    ? false
    : showTruncatedMessage && message.length > MAX_MESSAGE_LENGTH;
  $: truncatedMessage = truncateMessage
    ? message.substring(0, MAX_MESSAGE_LENGTH)
    : message;

  $: formattedDate = date && formatReviewCardDate(date);

  $: reviewWallURL = IS_PROD
    ? `https://ravely.io/b/${businessKey}`
    : `http://localhost:5051/b/${businessKey}`;

  $: ratingType = isCelebrity ? RatingType.Love : RatingType.Star;

  function showFullMessage() {
    showTruncatedMessage = false;
  }
</script>

<section class="container {$$restProps.class}" style={$$restProps.style}>
  {#if recommended === true || recommended === false}
    <section class="ratingContainer">
      <Like class="likeIcon {!recommended ? 'isNegative' : ''}" />
    </section>
  {/if}

  {#if rating && ratingMax}
    <section class="ratingContainer">
      <Rating type={ratingType} {rating} {ratingMax} />
    </section>
  {/if}

  {#if isSeeAll}
    <article class="messageBody">
      <div class="messagePreview">
        <span>
          {truncatedMessage.slice(0, 10)}
        </span>
        <i class="greyBlock">▇▇▇▇▇</i>
        <i class="greyBlock">▇▇▇▇▇▇▇▇▇▇</i>
        <i class="greyBlock">▇▇▇▇▇▇▇▇</i>
      </div>
      <a href={reviewWallURL} target="_blank" class="seeAll">Tap to see more</a>
    </article>
  {:else}
    <article class="messageBody">
      <span class={truncateMessage ? '' : 'content'}>
        {truncatedMessage}
        {#if truncateMessage}…<span
            target="_blank"
            class="seeMoreLink"
            aria-roledescription=""
            on:click={showFullMessage}
          >
            see more</span
          >
        {/if}
      </span>
    </article>
  {/if}

  {#if user}
    <section class="userSection">
      <div class="avatarContainer">
        <Avatar img={user.avatarUrl || user.pictureUrl} />
      </div>

      <div class="userInfo">
        <div class="userName">{user.name}</div>
        {#if date}
          <div class="date text-caption">
            <span class="dateText">{formattedDate}</span>
            <ReviewSourceIcon type={reviewType} originUrl={reviewOriginUrl} />
          </div>
        {/if}
      </div>
    </section>
  {/if}
</section>

<style lang="scss">
  .container {
    display: flex;
    flex-direction: column;
  }

  .ratingContainer {
    display: flex;
    justify-content: center;
    margin-bottom: 12px;
    font-size: 1.2rem;

    :global {
      .likeIcon {
        width: 20px;
        height: 20px;
        fill: var(--positive);

        &.isNegative {
          transform: rotate(180deg);
          fill: var(--negative);
        }
      }
    }
  }

  .messageBody {
    display: flex;
    flex-grow: 1;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    margin-bottom: 8px;
  }

  .messagePreview {
    width: 100%;
  }

  .greyBlock {
    color: var(--light-3);
    word-break: break-all;
    font-style: normal;

    &:nth-of-type(2) {
      color: var(--light-2);
    }
    &:nth-of-type(3) {
      color: var(--light-3);
    }
  }

  .seeAll {
    color: var(--ravely-primary);
    font-weight: bold;
    font-size: 1.1em;
  }

  .avatarContainer {
    display: inline-block;
    margin-right: 8px;
  }

  .userSection {
    display: flex;
    justify-content: center;
  }

  .userInfo {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .userName {
    font-weight: bold;
  }

  .seeMoreLink {
    color: var(--light-1);
    cursor: pointer;
  }

  .date {
    display: flex;
    align-items: center;
    color: var(--light-1);
  }

  .dateText {
    margin-right: 4px;
  }
</style>
